import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ApiError } from "@/api/client";
import { resolveUnattachedAmlCase, type UnattachedAmlAction } from "@/api/unattached-deposits";
import { type UnattachedDepositDetailResponse } from "@/api/types";

// feature-037: the AML officer's three decisions on a held unattached
// deposit — one dialog, three wordings. Decision-only (an optional note), same
// as the invoice and channel-payment AML dialogs: a refund's payout address
// comes later from the merchant or the customer.
const COPY: Record<UnattachedAmlAction, { title: string; description: string; confirm: string; done: string; destructive?: boolean }> = {
  accept: {
    title: "Accept this deposit?",
    description:
      "It returns to the merchant, who credits or refunds it — their automatic policy applies. The deposit must no longer be frozen.",
    confirm: "Accept",
    done: "Deposit accepted.",
  },
  reject: {
    title: "Reject this deposit?",
    description: "The deposit becomes REJECTED and its funds stay frozen. This cannot be undone from the backoffice.",
    confirm: "Reject",
    done: "Deposit rejected.",
    destructive: true,
  },
  refund: {
    title: "Refund this deposit?",
    description:
      "A refund opens for the full amount; the merchant or the customer supplies the payout address. The deposit must no longer be frozen.",
    confirm: "Open refund",
    done: "Refund opened.",
  },
};

export function UnattachedAmlDialog({
  depositId,
  action,
  onOpenChange,
  onSuccess,
}: {
  depositId: string;
  action: UnattachedAmlAction | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: (data: UnattachedDepositDetailResponse) => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (action) {
      setNote("");
      setError(null);
    }
  }, [action]);

  const mutation = useMutation({
    mutationFn: (a: UnattachedAmlAction) => resolveUnattachedAmlCase(depositId, a, note.trim() || undefined),
    onSuccess: (data, a) => {
      toast.success(COPY[a].done);
      onSuccess(data);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError && err.code === "STILL_FROZEN"
          ? "The deposit is still frozen — unfreeze it in the Fireblocks Console first."
          : err instanceof ApiError
            ? err.message
            : "The action failed.",
      );
    },
  });

  const copy = action ? COPY[action] : null;
  return (
    <Dialog open={action !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        {copy && action && (
          <>
            <DialogHeader>
              <DialogTitle>{copy.title}</DialogTitle>
              <DialogDescription>{copy.description}</DialogDescription>
            </DialogHeader>
            {error && (
              <div className="rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]">{error}</div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="unattached-aml-note">Note (optional)</Label>
              <Textarea id="unattached-aml-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                Cancel
              </Button>
              <Button
                variant={copy.destructive ? "destructive" : "default"}
                onClick={() => mutation.mutate(action)}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : copy.confirm}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
